import React, { useState, useMemo } from 'react';
import { ConsultationList } from '../components/ConsultationList';
import { 
  useOpenUrgentConsultations, 
  useDoneUrgentConsultations, 
  useNeedPatientMeetingUrgentConsultations 
} from '../hooks/useUrgentConsulting';
import { useCurrentUser } from '../hooks/useAuth';
import { UrgentConsulting } from '../types/api';

export type TabType = 'open' | 'needPatientMeeting' | 'done';

interface DashboardScreenProps {
  onLogout: () => void;
}

export const DashboardScreen: React.FC<DashboardScreenProps> = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState<TabType>('open');
  const { data: openConsultations = [], isPending: openIsPending, error: openError, refetch: refetchOpen } = useOpenUrgentConsultations();
  const { data: doneConsultations = [], isPending: doneIsPending, error: doneError, refetch: refetchDone } = useDoneUrgentConsultations();
  const { data: needPatientMeetingConsultations = [], isPending: needPatientMeetingIsPending, error: needPatientMeetingError, refetch: refetchNeedPatientMeeting } = useNeedPatientMeetingUrgentConsultations();
  const currentUser = useCurrentUser();

  const getConsultationsForTab = () => {
    switch (activeTab) {
      case 'open':
        return openConsultations;
      case 'needPatientMeeting':
        return needPatientMeetingConsultations;
      case 'done':
        return doneConsultations;
      default:
        return [];
    }
  };

  const getIsPendingForTab = () => {
    switch (activeTab) {
      case 'open':
        return openIsPending;
      case 'needPatientMeeting':
        return needPatientMeetingIsPending;
      case 'done':
        return doneIsPending;
      default:
        return false;
    }
  };

  const getErrorForTab = () => {
    switch (activeTab) {
      case 'open':
        return openError;
      case 'needPatientMeeting':
        return needPatientMeetingError;
      case 'done':
        return doneError;
      default:
        return null;
    }
  };

  const getRefetchForTab = () => {
    switch (activeTab) {
      case 'open':
        return refetchOpen;
      case 'needPatientMeeting':
        return refetchNeedPatientMeeting;
      case 'done':
        return refetchDone;
      default:
        return () => {};
    }
  };

  const consultations = getConsultationsForTab();
  const isPending = getIsPendingForTab();
  const error = getErrorForTab();
  const refetch = getRefetchForTab();

  const getFilteredConsultations = useMemo((): UrgentConsulting[] => {
    return consultations; // All data is pre-filtered by dedicated API endpoints
  }, [consultations]);

  const getTabTitle = (tab: TabType): string => {
    switch (tab) {
      case 'open':
        return 'Open';
      case 'needPatientMeeting':
        return 'Need Patient Meeting';
      case 'done':
        return 'Done';
    }
  };

  const getTabCount = (tab: TabType): number => {
    switch (tab) {
      case 'open':
        return openConsultations.length;
      case 'needPatientMeeting':
        return needPatientMeetingConsultations.length;
      case 'done':
        return doneConsultations.length;
    }
  };

  const tabs: TabType[] = ['open', 'needPatientMeeting', 'done'];

  if (error) {
    return (
      <div className="min-h-screen bg-scrollBg flex items-center justify-center">
        <div className="text-center p-6 bg-white rounded-lg shadow-md max-w-md mx-auto">
          <div className="text-red-600 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Data</h3>
          <p className="text-gray-600 mb-4">{error.message}</p>
          <button
            onClick={() => refetch()}
            className="bg-darkGreen text-white px-4 py-2 rounded-lg hover:bg-opacity-90 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-scrollBg">
      {/* Header */}
      <div className="bg-darkGreen text-white p-4 shadow-lg">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold truncate">Urgent Consulting</h1>
              <p className="text-xs sm:text-sm opacity-90 hidden sm:block">Healthcare Consultation Management</p>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm opacity-90">
                  {currentUser ? `${currentUser.name}` : 'Doctor'}
                </p>
                <p className="text-xs opacity-75">Emergency Medicine</p>
              </div>
              <div className="relative">
                <button
                  onClick={onLogout}
                  className="w-8 h-8 sm:w-10 sm:h-10 bg-yellowBg rounded-full flex items-center justify-center flex-shrink-0 hover:bg-opacity-90 transition-colors"
                  title="Logout"
                >
                  <span className="text-darkGreen font-bold text-xs sm:text-sm">
                    {currentUser ? `${currentUser.name[0]}` : 'DR'}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto">
          <nav className="flex">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-4 px-6 text-center font-medium transition-all duration-200 border-b-2 ${
                  activeTab === tab
                    ? 'border-darkGreen text-darkGreen bg-primaryBg'
                    : 'border-transparent text-grayText hover:text-darkGreen hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <span className="hidden sm:inline">{getTabTitle(tab)}</span>
                  <span className="sm:hidden">{getTabTitle(tab).split(' ')[0]}</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                    activeTab === tab
                      ? 'bg-darkGreen text-white'
                      : 'bg-gray-200 text-grayText'
                  }`}>
                    {getTabCount(tab)}
                  </span>
                </div>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto p-4">
        <div className="mb-4 sm:mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-blackText mb-2">
                {getTabTitle(activeTab)} Consultations
              </h2>
              <p className="text-grayText text-sm">
                {isPending ? 'Loading...' : `${getFilteredConsultations.length} consultation${getFilteredConsultations.length !== 1 ? 's' : ''}`}
              </p>
            </div>
            <button
              onClick={() => refetch()}
              disabled={isPending}
              className="bg-darkGreen text-white px-3 py-1 rounded-lg text-sm hover:bg-opacity-90 transition-colors disabled:opacity-50"
              title="Refresh"
            >
              {isPending ? (
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
              ) : (
                'Refresh'
              )}
            </button>
          </div>
        </div>

        {isPending ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-darkGreen"></div>
          </div>
        ) : (
          <ConsultationList 
            consultations={getFilteredConsultations} 
            tabType={activeTab}
          />
        )}
      </div>
    </div>
  );
};